import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { buildApiUrl } from "../config/api";
import "./AdminOrders.css";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [error, setError] = useState("");

  const fetchOrders = async () => {
    setError("");

    try {
      const [ordersRes, downloadsRes] = await Promise.all([
        fetch(buildApiUrl("/orders/admin"), { credentials: "include" }),
        fetch(buildApiUrl("/downloads/admin"), { credentials: "include" })
      ]);

      const ordersData = await ordersRes.json();
      const downloadsData = await downloadsRes.json();

      if (!ordersRes.ok || !ordersData.success) {
        throw new Error(ordersData.message || "Failed to fetch orders.");
      }

      if (!downloadsRes.ok || !downloadsData.success) {
        throw new Error(downloadsData.message || "Failed to fetch downloads.");
      }

      setOrders(ordersData.orders || []);
      setDownloads(downloadsData.downloads || []);
    } catch (err) {
      setError(err.message || "Failed to fetch admin orders.");
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  return (
    <div className="admin-orders-page">
      <aside className="admin-orders-sidebar">
        <div className="admin-orders-brand">
          <span className="admin-logo-script">MayaChitro</span>
          <span className="admin-label">Admin Panel</span>
        </div>

        <nav className="admin-orders-menu">
          <Link to="/admin">Dashboard</Link>
          <Link to="/admin/products">Photos / Products</Link>
          <Link className="active" to="/admin/orders">Orders & Sales</Link>
          <a href="#downloads">Downloads</a>
          <a href="#settings">Settings</a>
        </nav>
      </aside>

      <main className="admin-orders-main">
        <header className="admin-orders-header">
          <div>
            <p className="eyebrow">Sales Control</p>
            <h1>Orders & Downloads</h1>
            <p>Track paid orders, Stripe sessions, download tokens, and download counts.</p>
          </div>
          <button onClick={fetchOrders}>Refresh</button>
        </header>

        {error && <div className="admin-orders-error">{error}</div>}

        <section className="admin-order-stats">
          <div><span>Total Orders</span><strong>{orders.length}</strong></div>
          <div><span>Paid Orders</span><strong>{orders.filter((o) => o.paymentStatus === "paid").length}</strong></div>
          <div><span>Download Tokens</span><strong>{downloads.length}</strong></div>
          <div><span>Total Downloads</span><strong>{downloads.reduce((sum, item) => sum + Number(item.downloadCount || 0), 0)}</strong></div>
        </section>

        <section className="admin-orders-card">
          <h2>Recent Orders</h2>

          <div className="admin-orders-table-wrap">
            <table className="admin-orders-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Payment</th>
                  <th>Items</th>
                  <th>Downloads</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <strong>#{order.id}</strong>
                      <small>{order.stripeSessionId || "No Stripe Session"}</small>
                    </td>
                    <td>{order.customerEmail || "Not available"}</td>
                    <td>${Number(order.amountTotal || 0).toFixed(2)} {order.currency}</td>
                    <td><span className={`status-pill ${order.paymentStatus}`}>{order.paymentStatus}</span></td>
                    <td>
                      {order.items?.map((item) => (
                        <small key={item.id}>{item.productTitle} — {item.licenseLabel}</small>
                      ))}
                    </td>
                    <td>{order.totalDownloads || 0}</td>
                    <td>{new Date(order.createdAt).toLocaleString()}</td>
                  </tr>
                ))}

                {!orders.length && (
                  <tr>
                    <td colSpan="7" className="empty-table">No orders yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="admin-orders-card" id="downloads">
          <h2>Download Tokens</h2>

          <div className="admin-orders-table-wrap">
            <table className="admin-orders-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Customer</th>
                  <th>File</th>
                  <th>Count</th>
                  <th>Status</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {downloads.map((download) => (
                  <tr key={download.id}>
                    <td>
                      <strong>{download.productTitle}</strong>
                      <small>Order #{download.orderId}</small>
                    </td>
                    <td>{download.customerEmail || "Not available"}</td>
                    <td>{download.privateFileKey || "File not attached"}</td>
                    <td>{download.downloadCount}/{download.maxDownloads}</td>
                    <td><span className={`status-pill ${download.status}`}>{download.status}</span></td>
                    <td>{download.expiresAt ? new Date(download.expiresAt).toLocaleString() : "N/A"}</td>
                  </tr>
                ))}

                {!downloads.length && (
                  <tr>
                    <td colSpan="6" className="empty-table">No download tokens yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminOrders;
